import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { StatusEnum } from "../../../../common/data/base/enums/statusEnum";
import MESSAGES from "../../../../common/data/constants/messages";
import { IEmployee } from "../../../../common/data/dtos/employee";
import { IWorkResource } from "../../../../common/data/dtos/work-resource";
import { IWorkResourceEmployee } from "../../../../common/data/dtos/work-resource-employee";
import ReturnStatusEnum from "../../../../common/data/enums/enumReturnStatus";
import WorkResourceEnum from "../../../../common/data/enums/enumWorkResouce";
import WorkResourceEmployeeStatusEnum from "../../../../common/data/enums/enumWorkResourceEmployeeStatus";
import Badge from "../../../../components/bootstrap/Badge";
import Button from "../../../../components/bootstrap/Button";
import Card, { CardFooterRight, CardTabItem } from "../../../../components/bootstrap/Card";
import FormGroup from "../../../../components/bootstrap/forms/FormGroup";
import GroupedSelect from "../../../../components/bootstrap/forms/GroupedSelect";
import Input from "../../../../components/bootstrap/forms/Input";
import InputGroup from "../../../../components/bootstrap/forms/InputGroup";
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from "../../../../components/bootstrap/Modal";
import Spinner from "../../../../components/bootstrap/Spinner";
import { showSuccess, showWarning } from "../../../../components/extras/Notifications";
import workResourceEmployeeService from "../../../../services/work-resource-employee-service";
import workResourceService from "../../../../services/work-resource-service";

type WorkResourceEmployeeFormValues = {
    quantity: number;
    workResourceId: string;
};

interface Props {
    isModalOpened: boolean;
    selectedEmployee: IEmployee | null;
    onModalClose: () => void;
    onAfterSubmit: () => void;
}

export const WorkResourceEmployeeModal = ({ isModalOpened, selectedEmployee, onModalClose, onAfterSubmit }: Props) => {

    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [workResources, setWorkResources] = useState<IWorkResource[]>([]);
    const [workResourcesEmployee, setWorkResourcesEmployee] = useState<IWorkResourceEmployee[]>([]);
    const [groupedWorkResources, setGroupedWorkResources] = useState<
        { label: string; options: { value: string; text: string }[] }[]
    >([]);

    const formik = useFormik({
        initialValues: {
            quantity: 1,
            workResourceId: '',
        },

        validate: (values) => {
            const errors: {
                quantity?: string;
                workResourceId?: string;
            } = {};

            if (!values.quantity)
                errors.quantity = MESSAGES.VALIDATION.REQUIRED_FIELD;

            return errors;
        },

        onSubmit: (values, { resetForm }) => {
            handleAddClick(values);
            resetForm();
        },
    });

    useEffect(() => {

        if (selectedEmployee) {
            resetForm();
            fetchData();
        }

    }, [isModalOpened]);

    const fetchData = async () => {

        setIsSubmitting(true);

        try {
            if (!selectedEmployee?.id) {
                showWarning(MESSAGES.VALIDATION.PARAMETERS_ERROR_VALIDATION)
            } else {

                const [workResourcesResponse, workResourceEmployeeResponse] = await Promise.all([
                    workResourceService.getWorkResources(),
                    workResourceEmployeeService.getWorkResourceEmployeeByEmployee(selectedEmployee.id),
                ]);

                if (workResourcesResponse.error) {
                    showWarning(workResourcesResponse.error);
                    return;
                }

                if (workResourceEmployeeResponse.error) {
                    showWarning(workResourceEmployeeResponse.error);
                    return;
                }

                setWorkResources(workResourcesResponse.workResources || [])

                if (selectedEmployee.jobTitle &&
                    (selectedEmployee.jobTitle.workResourcesJobTitles &&
                        selectedEmployee.jobTitle.workResourcesJobTitles.length > 0)) {

                    const workResourcesJobTitlesIds = selectedEmployee.jobTitle.workResourcesJobTitles.map(p => p.workResourceId);

                    const employeeWorkResources = workResourcesResponse.workResources?.filter(
                        (p) => p.id && workResourcesJobTitlesIds.includes(p.id)
                    );

                    const allWorkResources = workResourcesResponse.workResources?.filter(
                        (p) => p.id && !workResourcesJobTitlesIds.includes(p.id)
                    );

                    const groups = [];

                    if (employeeWorkResources && employeeWorkResources.length > 0) {
                        groups.push({
                            label: 'Função',
                            options: employeeWorkResources.map(item => ({
                                value: item.id!,
                                text: item.description
                            }))
                        });
                    }

                    if (allWorkResources && allWorkResources.length > 0) {
                        groups.push({
                            label: 'Geral',
                            options: allWorkResources.map(item => ({
                                value: item.id!,
                                text: item.description
                            }))
                        });
                    }

                    setGroupedWorkResources(groups);
                } else {

                    const groups = [];

                    if (workResourcesResponse.workResources && workResourcesResponse.workResources.length > 0) {
                        groups.push({
                            label: 'Geral',
                            options: workResourcesResponse.workResources.map(item => ({
                                value: item.id!,
                                text: item.description
                            }))
                        });
                    }
                    setGroupedWorkResources(groups || []);
                }

                setWorkResourcesEmployee(workResourceEmployeeResponse.workResourceEmployees || []);
            }
        } catch (error) {
            console.error('Erro nas requisições:', error);
            showWarning('Erro ao carregar dados');
        } finally {
            setIsSubmitting(false);
        }

    }

    const fetchWorkResourcesEmployee = async () => {

        if (selectedEmployee?.id) {

            const response = await workResourceEmployeeService.getWorkResourceEmployeeByEmployee(selectedEmployee?.id)

            if (response.error) {
                showWarning(response.error);
                return;
            }

            setWorkResourcesEmployee(response.workResourceEmployees || []);
        }
    }

    const onFormSubmit = async () => {
        if (!workResourcesEmployee) {
            showWarning(MESSAGES.VALIDATION.EMPTY_COLLECTION)
            return;
        }

        const itensToAdd = workResourcesEmployee
            .filter(item => !item.id || item.id.trim() === '')
            .map(({ workResource, ...toAdd }) => toAdd);


        if (!itensToAdd || itensToAdd.length === 0) {
            showWarning(MESSAGES.VALIDATION.EMPTY_COLLECTION_TO_ADD)
            return;
        }

        setIsSubmitting(true);

        const response = await workResourceEmployeeService.createManyWorkResourceEmployee(itensToAdd);

        setIsSubmitting(false);

        if (response.error) {
            showWarning(MESSAGES.ACTION.SAVE_ERROR);
            return;
        }

        showSuccess(MESSAGES.ACTION.SAVED);
        resetForm()
        onAfterSubmit();
    }

    const resetForm = () => {
        formik.resetForm({
            values: {
                quantity: 1,
                workResourceId: ''
            },
        });
    };

    const onClose = () => {
        onModalClose();
    };

    const handleAddClick = async (values: WorkResourceEmployeeFormValues) => {

        if (!selectedEmployee?.id || !values.workResourceId) {
            showWarning(MESSAGES.VALIDATION.PARAMETERS_ERROR_VALIDATION);
            return;
        }

        const exists = workResourcesEmployee.some(item => item.workResourceId === values.workResourceId && item.returnStatus === ReturnStatusEnum.none);
        if (exists) {
            showWarning(MESSAGES.VALIDATION.ITEM_EXISTS);
            return;
        } else {

            const selectedWorkResource = workResources.find(p => p.id === values.workResourceId);

            const newWorkResourceEmployee: IWorkResourceEmployee = {
                employeeId: selectedEmployee?.id,
                quantity: values.quantity ?? 1,
                workResourceId: values.workResourceId,
                rowVersion: 1,
                status: StatusEnum.Active,
                workResource: selectedWorkResource,
                situation: WorkResourceEmployeeStatusEnum.notDelivered,
                returnStatus: ReturnStatusEnum.none
            };

            setWorkResourcesEmployee((prev) => [
                ...prev,
                newWorkResourceEmployee,
            ]);
        }
    }

    const handleDeleteClick = async (workResourceEmployee: IWorkResourceEmployee) => {

        if (!workResourceEmployee.id) {
            const filteredList = workResourcesEmployee.filter(item => item.workResourceId !== workResourceEmployee.workResourceId);
            setWorkResourcesEmployee(filteredList);
        } else {

            setIsSubmitting(true);

            const response = await workResourceEmployeeService.deleteWorkResourceEmployee(workResourceEmployee.id);

            setIsSubmitting(false);

            if (response.error) {
                showWarning(response.error);
                return;
            }

            showSuccess(MESSAGES.ACTION.DELETED);
            fetchWorkResourcesEmployee();
        }
    }

    return (
        <Modal isOpen={isModalOpened} setIsOpen={onClose} size={'xl'} isCentered isStaticBackdrop>
            <ModalHeader setIsOpen={onClose}>
                <ModalTitle id='work-resource-employee-modal'>Manutenção de ferramentas e EPI do colaborador</ModalTitle>
            </ModalHeader>
            <ModalBody>
                <Card hasTab tabButtonColor='primary'>
                    <CardTabItem id='tab-item-1' title={selectedEmployee?.name || 'Manutenção de ferramentas e EPI do colaborador'} icon='PeopleAlt'>

                        <div className='row g-4'>

                            <div className='col-lg-4 col-md-4 col-sm-12'>
                                <FormGroup id='quantity' label='Quantidade' isFloating>
                                    <Input
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.quantity}
                                        isValid={formik.isValid}
                                        isTouched={formik.touched.quantity}
                                        invalidFeedback={formik.errors.quantity}
                                        validFeedback=''
                                        type="number"
                                    />
                                </FormGroup>
                            </div>

                            <div className='col-lg-8 col-md-8 col-sm-12'>
                                <InputGroup
                                    isWrap={true}
                                >
                                    <FormGroup id='workResourceId' label='Ferramentas e EPI' isFloating>
                                        <GroupedSelect

                                            ariaLabel='Escolha uma ferramentas ou EPI'
                                            list={groupedWorkResources}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.workResourceId}
                                        />
                                    </FormGroup>
                                    <Button
                                        icon='AddCircleOutline'
                                        color='primary'
                                        onClick={formik.handleSubmit}
                                    >
                                        Adicionar
                                    </Button>
                                </InputGroup>
                            </div>

                            <div className='col-lg-12 col-md-12 col-sm-12 table-responsive '>

                                <table className='table table-modern table-hover'>
                                    <thead>
                                        <tr>
                                            <th>Quantidade</th>
                                            <th>Descrição</th>
                                            <th>CA</th>
                                            <th>Tipo</th>
                                            <td aria-labelledby='Actions' />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {workResourcesEmployee
                                            .filter(item => item.situation === WorkResourceEmployeeStatusEnum.notDelivered)
                                            .map((item: IWorkResourceEmployee) => (
                                                <tr key={item.id}>
                                                    <td>{item.quantity}</td>
                                                    <td>{item.workResource?.description}</td>
                                                    <td>{item.workResource?.approvalCertificate || '-'}</td>
                                                    <td>
                                                        {item.workResource?.type === WorkResourceEnum.tool ? (
                                                            <h5>
                                                                <Badge color='info' className='align-items-center justify-content-center'>
                                                                    Ferramenta
                                                                </Badge>
                                                            </h5>
                                                        ) : (
                                                            <h5>
                                                                <Badge color='info' className='align-items-center justify-content-center'>
                                                                    EPI
                                                                </Badge>
                                                            </h5>
                                                        )}
                                                    </td>


                                                    <td className='text-end'>
                                                        <Button
                                                            color='dark'
                                                            isLight
                                                            icon='Delete'
                                                            onClick={() => handleDeleteClick(item)}
                                                            aria-label='Remover'
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                    </tbody>
                                </table>
                            </div>

                        </div>
                    </CardTabItem>
                </Card>
            </ModalBody>
            <ModalFooter className='bg-transparent'>
                <CardFooterRight>
                    <Button color='success' className='w-100' onClick={onFormSubmit} isDisable={isSubmitting}>
                        {isSubmitting && <Spinner isSmall inButton isGrow />}
                        Salvar
                    </Button>
                </CardFooterRight>
            </ModalFooter>
        </Modal>
    );
}